// Spend/SpendDonutChart.jsx
import { Box, Stack, Typography } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import theme from "../../../../theme";

const DONUT_COLORS = [
  theme.palette.primary.main,            // #151A40
  theme.palette.secondary.sunriseOrange, // #EFA500
  theme.palette.secondary.tiffanyBlue,   // #0DC3B2
  theme.palette.primary.brandSecondary,  // #8F7EF8
  theme.palette.secondary.lawnGreen,     // #AED135
  theme.palette.secondary.berry,         // #B8117E
];
const FONT = theme.typography.fontFamily;

export const SpendDonutChart = ({ columns, rows }) => {
  const [nameCol, valueCol] = columns;

  const data = (rows ?? [])
    .map((r) => ({
      name: r[nameCol.key] ?? "N/A",
      value: Number(r[valueCol.key]) || 0,
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  if (!data.length) {
    return (
      <Typography sx={{ color: "text.secondary" }}>
        No cost drivers data available.
      </Typography>
    );
  }

  const total = data.reduce((s, d) => s + d.value, 0);
  const pct = (v) => (total ? (v / total) * 100 : 0);

  // % inside slice; hidden on tiny slices (legend still shows them)
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    const p = pct(value);
    if (p < 5) return null;
    const RAD = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    return (
      <text
        x={x} y={y}
        fill={theme.palette.primary.white}
        textAnchor="middle" dominantBaseline="central"
        style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600 }}
      >
        {`${p.toFixed(1)}%`}
      </text>
    );
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      sx={{ alignItems: "center", width: "100%" }}
    >
      {/* Donut: fixed height so ResponsiveContainer always has a size */}
      <Box sx={{ width: { xs: "100%", md: "55%" }, height: 300, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
              stroke={theme.palette.primary.white}
              strokeWidth={2}
              labelLine={false}
              label={renderLabel}
              isAnimationActive={false}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${pct(value).toFixed(1)}%`, name]}
              contentStyle={{
                fontFamily: FONT,
                borderRadius: theme.borderRadius.small,
                border: `1px solid ${theme.palette.gray.medium}`,
                boxShadow: theme.customShadows.medium,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Custom MUI legend: reliable + themed, stacks below on small screens */}
      <Stack spacing={1} sx={{ width: { xs: "100%", md: "45%" }, px: { xs: 2, md: 0 } }}>
        {data.map((d, i) => (
          <Stack key={d.name} direction="row" sx={{ alignItems: "center" }} spacing={1.5}>
            <Box
              sx={{
                width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length],
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: "primary.main", fontFamily: FONT }}
            >
              {d.name} — {pct(d.value).toFixed(1)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};